/**
 * Hub slide catalog and grid placement.
 *
 * Title mosaic placements live in TitleSlide.css (Arizona-style absolute
 * panels). `title` cells below are still passed to IntroCovers for the note
 * tiling. Loudoun uses `loudounCells` inside LoudounSlide.
 */

export type SlideId = "title" | "virginia" | "utah" | "arizona";

/**
 * Chapter, the intro note, or the closing credits/footer.
 * Note and credits have no grid.
 */
export type DeckSlideId = "note" | SlideId | "credits";

export type GridCellPlacement = {
  id: string;
  /** CSS `grid-column` value, e.g. `"1"` or `"1 / 3"`. */
  column: string;
  /** CSS `grid-row` value, e.g. `"1"` or `"2"`. */
  row: string;
  /** Optional label for placeholder scaffolding. */
  label?: string;
  /** Optional internal split (title-slide art only for now). */
  split?: "horizontal" | "vertical";
};

export type HubSlide = {
  id: SlideId;
  label: string;
  /** When set, chapter nav can deep-link out to the published feature. */
  featureHref?: string | null;
  cells: ReadonlyArray<GridCellPlacement>;
};

/** Default 3×2 with bottom-left+middle merged (title / feature pattern). */
const titleCells: ReadonlyArray<GridCellPlacement> = [
  { id: "tl", column: "1", row: "1", label: "TL", split: "horizontal" },
  { id: "tm", column: "2", row: "1", label: "TM" },
  { id: "tr", column: "3", row: "1", label: "TR" },
  { id: "bl-merged", column: "1 / 3", row: "2", label: "BL+BM (merged)" },
  { id: "br", column: "3", row: "2", label: "BR", split: "horizontal" },
];

/**
 * Loudoun / Virginia: merged left (photo grows, byline plate flex:0) + BR still.
 * Dek is a content-sized overlay plate below the package nav — not a grid cell.
 */
export const loudounCells: ReadonlyArray<GridCellPlacement> = [
  { id: "left", column: "1", row: "1 / 3", label: "Left" },
  { id: "br", column: "3", row: "2", label: "BR" },
];

export const slides: ReadonlyArray<HubSlide> = [
  {
    id: "title",
    label: "Title",
    cells: titleCells,
  },
  {
    id: "virginia",
    label: "Virginia",
    featureHref:
      "/cs/features/975597/loudoun-county-virginia-data-center-backlash",
    cells: loudounCells,
  },
  {
    id: "utah",
    label: "Utah",
    featureHref:
      "/cs/features/993343/stratos-utah-kevin-oleary-data-center-backlash",
    // UtahSlide lays out with absolute fractions, not the 3×2 grid.
    cells: [],
  },
  {
    id: "arizona",
    label: "Arizona",
    featureHref: "/cs/features/1016650/arizona-water-data-center-tsmc",
    // ArizonaSlide lays out with absolute fractions, not the 3×2 grid.
    cells: [],
  },
] as const;

/** Motion-on order. The intro note leads when the story has one; credits close it. */
export function deckSlides(
  hasNote: boolean,
): ReadonlyArray<{ id: DeckSlideId; label: string }> {
  const chapters = slides.map((slide) => ({
    id: slide.id,
    label: slide.label,
  }));
  const sequence = [...chapters, { id: "credits" as const, label: "Credits" }];
  if (!hasNote) {
    return sequence;
  }
  return [{ id: "note", label: "Intro" }, ...sequence];
}
